import React, { useState } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { Button, Col, Form, Row } from 'react-bootstrap'
import InputWithValidation from '../../../components/forms/InputWithValidation'
import SelectWithValidation from '../../../components/forms/SelectWithValidation'
import { findOption } from '../../../utils/Select'
import Notifier from '../../../components/Notifier'
import TaskResultController from '../../../controllers/TaskResultController'
import {
  SelectionState,
  IntegratedSelection,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableSelection,
} from '@devexpress/dx-react-grid-bootstrap4';
import '@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css';
import UserController from '../../../controllers/UserController';
import TaskResult from '../../../models/TaskResult'

const validationSchema = Yup.object().shape({
  userId: Yup.string().nullable().required('Please select a user!'),
  amount: Yup.string().required('Amount cannot be empty!').nullable(),
  comment: Yup.string()
  .required('Please type a comment for the payment!')
  .nullable(),
});

const ProjectPaymentCreationFormView = ({ users, projectId, handleSubmit }) => {
  const [paymentData, setPaymentData] = useState(null);
  const [taskResultTableData, setTaskResultTableData] = useState([]);
  const [selectedUserId , setSelectedUserId] = useState(null);
  const [userPaymentMethod, setUserPaymentMethod] = useState('');
  const initialValues = {
    userId: paymentData?.userId ?? null,
    amount: paymentData?.amount ?? '',
    comment: paymentData?.comment ?? '',
    taskResultIds: paymentData?.taskResultIds ?? null
  };

  const userOptions = users;

  const getPaymentProfile = async (userId) => {
    try {
      await UserController.adminGetUserPaymentProfile(userId).then(r => {
        setUserPaymentMethod(r.method);
      }).catch(e =>  Notifier.error(e.message));
    } catch (e) {
      setUserPaymentMethod('');
      Notifier.error(e)
    }
  }

  const getTaskResult = async (userId) => {
    try {
      await TaskResultController.getProjectTaskResults(projectId, {
        'worker.id': userId,
      }).then(r => {
        setTaskResultTableData(r.content.map(item => ({
          id: item.id,
          evaluationStatus: item.taskResult?.evaluationStatus,
          paymentStatus: item.taskResult?.paymentStatus,
          credits: item.taskResult?.credits
        })))
      });
    }
    catch (e) {
      Notifier.error(e.message);
    }
  }

  const handleChange = (value) => {
    setSelectedUserId(value);
    getTaskResult(value);
    getPaymentProfile(value);
  }

  const onHandleSubmit = (values, formik) => {
    let data = {
      userId: +values.userId,
      amount: values.amount,
      comment: values.comment,
      taskResultIds: values.taskResultIds
    };
    handleSubmit(data)
    formik.resetForm();
    setSelection([]);
    setTaskResultTableData([]);
    setUserPaymentMethod('');
    setPaymentData(null);
  };

  const columns = [
    {
      name: 'id',
      title: 'Task Result ID',
      orderable: true,
      getCellValue: (row) => row.id,
    },
    {
      name: 'evaluationStatus',
      title: 'Evaluation Status',
      orderable: true,
      getCellValue: (row) => row.evaluationStatus,
    },
    {
      name: 'paymentStatus',
      title: 'Payment Status',
      orderable: true,
      getCellValue: (row) => row.paymentStatus,
    },
    {
      name: 'credits',
      title: 'Credits',
      orderable: true,
      getCellValue: (row) => row.credits,
    },
  ];

  const cellComponent = (props) => {
    return <Table.Cell {...props} className='tw-p-[6px]' />;
  };

  const [selection, setSelection] = useState([]);

  const handleSelection = (selection) => {
    const totalAmount = selection.map(s => taskResultTableData[s].credits).reduce((curr, acc)  => {
      return curr + acc;
    }, 0);
    const taskResultIds = selection.map(s => taskResultTableData[s].id)

    setPaymentData(prev => { return {
      ...prev,
      userId: selectedUserId,
      amount : totalAmount,
      comment : taskResultIds.join(),
      taskResultIds : taskResultIds
    }})
    setSelection(selection);
  }


  const rowSelectionEnabled = row => (row.paymentStatus !== TaskResult.PAYMENT_STATUS.SUBMITTED && row.paymentStatus !== TaskResult.PAYMENT_STATUS.COMPLETED);
  const  PatchedTableSelection =  (props) => {
      const { rowSelectionEnabled, ...restProps } = props;
      return (
        <TableSelection
          cellComponent={ props =>
          rowSelectionEnabled(props.tableRow.row)
              ? (
                <TableSelection.Cell { ...props } />
              ) : (
                <Table.StubCell { ...props } />
              )
          }
          {...restProps}
        />
      );
  }

  return (
    <>
      <div className='tw-grid md:tw-grid-cols-2 sm:tw-grid-cols-1 tw-gap-4'>
        <div className='tw-border tw-border-solid tw-border-gray-200 tw-p-4 tw-rounded-lg'>
          <div className='tw-font-medium tw-text-lg tw-mb-4'>Create a new payment</div>
            <Formik
              className='tw-relative tw-z-0'
              initialValues={initialValues}
              enableReinitialize={true}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) => {
                onHandleSubmit(values, { resetForm });
              }}
            >
              {(props) => {
                return (
                  <div className=''>
                    <Form onSubmit={props.handleSubmit}>
                      <span className='tw-text-xs tw-text-blue-500'>{  userPaymentMethod ? `The user chose ${userPaymentMethod} as their preferred payment method.` :
                        props.values?.userId ? 'The user has no payment method defined in their profile.' : null}</span>
                      <Row>
                        <Col sm={12} md={12}>
                          <SelectWithValidation
                            id='UserId'
                            propertyName='userId'
                            label='UserId'
                            helpText='Set a user for this payment'
                            options={userOptions}
                            defaultOption={findOption(
                              userOptions,
                              paymentData?.userId ?? props.values.userId
                            )}
                            defaultValue={props.values.userId}
                            touched={props.touched.userId}
                            error={props.errors.userId}
                            onChange={props.setFieldValue}
                            onValueChange={handleChange}
                            onBlur={props.setFieldTouched}
                          />
                        </Col>
                        <Col sm={12} md={12}>
                          <InputWithValidation
                            id='amount'
                            propertyName='amount'
                            label='Amount'
                            placeholder='Amount'
                            helpText='Enter a amount for creating the payment request'
                            defaultValue={props.values.amount}
                            touched={props.touched.amount}
                            error={props.errors.amount}
                            onChange={props.setFieldValue}
                            onBlur={props.setFieldTouched}
                          />
                        </Col>
                        <Col sm={12} md={12}>
                          <InputWithValidation
                            id='comment'
                            propertyName='comment'
                            label='Comment'
                            placeholder='Comment'
                            helpText='Enter a Comment for this payment request'
                            defaultValue={props.values.comment}
                            touched={props.touched.comment}
                            error={props.errors.comment}
                            onChange={props.setFieldValue}
                            onBlur={props.setFieldTouched}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col className='tw-flex tw-justify-end'>
                          <Button
                            className={props.isValid ? 'btn-success' : 'btn-secondary'}
                            type='submit'
                            disabled={!props.isValid}
                          >
                            Create
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                );
              }}
            </Formik>
        </div>
        <div className='tw-border tw-border-solid tw-border-gray-200 tw-p-4 tw-rounded-lg tw-overflow-y-scroll'>
          <div className='tw-font-medium tw-text-lg tw-mb-4'>Associate Payment with Task Results</div>
            <div>
              <Grid
                rows={taskResultTableData}
                columns={columns}
              >
                <SelectionState
                  selection={selection}
                  onSelectionChange={handleSelection}
                />
                <IntegratedSelection />
                <Table
                  columnExtensions={[
                    {
                      width: 'auto',
                      columnName: 'id',
                    },
                    {
                      width: 'auto',
                      columnName: 'evaluationStatus',
                    },
                    {
                      width: 'auto',
                      columnName: 'paymentStatus',
                    },
                    {
                      width: 'auto',
                      columnName: 'credits',
                      wordWrapEnabled: true,
                    },
                  ]}
                  cellComponent={cellComponent}
                />
                <TableHeaderRow />
                <PatchedTableSelection
                  rowSelectionEnabled={rowSelectionEnabled}
                />
              </Grid>
            </div>
          </div>
      </div>
    </>
);
}
export default ProjectPaymentCreationFormView;